import React from 'react';
import PropTypes from 'prop-types';
import Card from './Card';
import Badge from './Badge';
import Avatar from './Avatar';

const OpportunityCard = ({ opportunity, onClick }) => {
  const {
    id,
    title,
    position_type,
    location,
    remote,
    description,
    compensation,
    user,
    published_at,
    duration,
  } = opportunity;

  const positionTypeBadgeVariant = position_type === 'internship' ? 'internship' : 'full-time';
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card
      variant="interactive"
      padding="md"
      className="h-full flex flex-col"
      onClick={() => onClick && onClick(id)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate">
            {title}
          </h3>
          <div className="flex flex-wrap gap-2 mb-2">
            <Badge variant={positionTypeBadgeVariant} size="sm">
              {position_type === 'internship' ? 'Internship' : 'Full-Time'}
            </Badge>
            {remote && (
              <Badge variant="info" size="sm">
                Remote
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Advisor Info */}
      {user && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
          <Avatar src={user.avatar} name={user.name} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.name}
            </p>
            {user.company && (
              <p className="text-xs text-gray-600 truncate">{user.company}</p>
            )}
          </div>
        </div>
      )}

      {/* Details */}
      <div className="space-y-2 mb-4 flex-1">
        <div className="flex items-start gap-2 text-sm">
          <svg
            className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-gray-700">{location}</span>
        </div>

        {duration && (
          <div className="flex items-start gap-2 text-sm">
            <svg
              className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-gray-700">{duration}</span>
          </div>
        )}

        {compensation && (
          <div className="flex items-start gap-2 text-sm">
            <svg
              className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-gray-700">{compensation}</span>
          </div>
        )}

        {description && (
          <p className="text-sm text-gray-600 line-clamp-2 mt-3">{description}</p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs text-gray-500">
        <span>Posted {formatDate(published_at)}</span>
        <span className="text-blue-600 font-medium hover:text-blue-700">
          View Details →
        </span>
      </div>
    </Card>
  );
};

OpportunityCard.propTypes = {
  opportunity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    position_type: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    remote: PropTypes.bool,
    description: PropTypes.string,
    compensation: PropTypes.string,
    duration: PropTypes.string,
    published_at: PropTypes.string,
    user: PropTypes.shape({
      name: PropTypes.string,
      avatar: PropTypes.string,
      company: PropTypes.string,
    }),
  }).isRequired,
  onClick: PropTypes.func,
};

export default OpportunityCard;


